import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import useAuditLog from '../../hooks/use-audit-log';
import { useTranslation } from 'react-i18next';
import {
  Table,
  Pagination,
} from 'react-bootstrap';
import './AuditLog.scss';
import { AuditLogRow } from './AuditLogRow';
import DateTimePicker from 'react-datetime';
import { getLocaleName } from '../../utils/date';
import moment from 'moment-timezone';
import { useStores } from '../../hooks/use-stores';
import { Moment } from 'moment';

export type AuditLogActionFilter = 'Create' | 'Update' | 'Delete';

export interface AuditLogProps {
  modelId?: number[] | number | null;
  modelName?: string[] | string;
  user?: string;
  action?: AuditLogActionFilter;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

const AuditLog: React.FC<AuditLogProps> = ({
  modelId,
  modelName,
  user,
  action,
  pageSize,
}) => {
  const { t } = useTranslation();
  const { settingsStore, newAuthStore } = useStores();
  const [dateFrom, setDateFrom] = useState<Moment>(
    moment().startOf('day').subtract(7, 'days'),
  );
  const [dateTo, setDateTo] = useState<Moment>(
    moment().endOf('day'),
  );
  const [activePage, setActivePage] = useState(1);

  const initializeDates = useCallback(() => {
    const timezone = settingsStore.configuration?.ianaTimeZone || moment.tz.guess();
    const newDateFrom = moment().tz(timezone).startOf('day').subtract(7, 'days');
    const newDateTo = moment().tz(timezone).endOf('day');
    setDateFrom(newDateFrom);
    setDateTo(newDateTo);
  }, [settingsStore.configuration?.ianaTimeZone]);

  useEffect(() => {
    if (settingsStore.configuration && (!dateFrom || !dateTo)) {
      initializeDates();
    }
  }, [settingsStore.configuration, dateFrom, dateTo, initializeDates]);

  const onChangeDateFrom = useCallback((value: Moment | string) => {
    if (moment.isMoment(value)) {
      setDateFrom(value as Moment);
    }
  }, []);

  const onChangeDateTo = useCallback((value: Moment | string) => {
    if (moment.isMoment(value)) {
      setDateTo(value as Moment);
    }
  }, []);

  const auditLogParams = useMemo(
    () => ({
      modelId,
      modelName,
      user,
      action,
      from: dateFrom.format('YYYY-MM-DD'),
      to: dateTo.format('YYYY-MM-DD'),
      page: activePage,
      pageSize,
    }),
    [modelId, modelName, user, action, dateFrom, dateTo, activePage, pageSize],
  );

  const {
    data: auditLogData,
    loading: auditLogLoading ,
    error: auditLogError,
  } = useAuditLog(auditLogParams);

  const totalPages = auditLogData?.pagination.pages || 1;

  return (
    <>
      <div className="input-group input-large date-picker input-daterange">
        <DateTimePicker
          inputProps={{ className: 'form-control', placeholder: 'Date from' }}
          onChange={onChangeDateFrom}
          timeFormat={false}
          value={dateFrom}
          locale={getLocaleName(newAuthStore.user?.language)}
        />
        <span className="input-group-addon"> - </span>
        <DateTimePicker
          inputProps={{ className: 'form-control', placeholder: 'Date to' }}
          onChange={onChangeDateTo}
          timeFormat={false}
          value={dateTo}
          locale={getLocaleName(newAuthStore.user?.language)}
        />
      </div>
      <div className="audit-log-table-container">
        <Table>
          <thead>
            <tr>
              <th></th>
              <th>{t('Date')}</th>
              <th>{t('User')}</th>
            </tr>
          </thead>
          <tbody>
            {auditLogLoading && (
              <tr>
                <td colSpan={3}>{t('Loading')}...</td>
              </tr>
            )}
            {auditLogError && (
              <tr>
                <td colSpan={3}>
                  <p className={'text-center'}>
                    {t('There was an error loading AuditLog data. Make sure you have the permissions to view this page.')}
                  </p>
                </td>
              </tr>
            )}
            {auditLogData?.items.length === 0 && !auditLogLoading && (
              <tr>
                <td colSpan={3}>
                  <div
                    className="audit-log-no-records"
                  >
                    {t('No records found')}
                  </div>
                </td>
              </tr>
            )}
            {auditLogData?.items.map((item) => (
              <AuditLogRow
                key={`audit-log-row-item-${item.actionDate}`}
                item={item}
              />
            ))}
          </tbody>
        </Table>
      </div>
      <Pagination>
        <Pagination.First disabled={activePage === 1} onClick={() => setActivePage(1)} />
        <Pagination.Prev
          disabled={activePage === 1}
          onClick={() => setActivePage((prev) => Math.max(prev - 1, 1))}
        />
        {[...Array(totalPages)].map((_, index) => (
          <Pagination.Item
            key={`pagination-item-page-${index + 1}`}
            active={activePage === index + 1}
            onClick={() => setActivePage(index + 1)}
          >
            {index + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          disabled={activePage === totalPages}
          onClick={() => setActivePage((prev) => Math.min(prev + 1, totalPages))}
        />
        <Pagination.Last
          disabled={activePage === totalPages}
          onClick={() => setActivePage(totalPages)}
        />
      </Pagination>
    </>
  );
};

export default AuditLog;
